import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/lib/icons";
import { fillCopy } from "./contribution-kit-copy";
import { TaskHelpPills } from "./TaskHelpPills";
import { TaskSwitcher } from "./TaskSwitcher";
import { taskPosition } from "./task-progress";
import { findContributorTask, type ContributionTaskKey } from "./task-registry";
import { useKitCopy } from "./use-kit-copy";
import { useTaskFocusMode } from "./task-focus-mode";

/**
 * Compact header of a task page with back, task, help, focus toggle, and progress controls.
 *
 * ```tsx
 * <TaskHeader
 *   task="speak"
 *   index={currentSentence} total={sentences.length}
 *   progress={percentDone}
 *   itemId={wordItem?.id}
 *   meta={<span>Ewe</span>}
 * />
 * ```
 *
 * - `task`: key of contributorTasks(); gives the label, icon and help content.
 * - `index` / `total`: 0-based index of the item on screen and batch size;
 *   omit both when there is no batch yet.
 * - `positionLabel`: replaces the default "N / total" text (e.g. t.sentenceXofY).
 * - `progress`: 0..100, share of the batch already handled; hidden if omitted.
 * - `itemId`: saved with a "Signaler" report.
 * - `onBack`: defaults to the contributor home (`/:lang`).
 * - `meta`: small line next to the position (language, mode badge...).
 * - `extraGuidelines`: localised lines appended to the task's rules.
 * - Inside TaskShell, focus mode replaces task switching and help pills with the task name.
 */
export function TaskHeader({
  task,
  index,
  total,
  positionLabel,
  progress,
  itemId,
  onBack,
  meta,
  extraGuidelines,
}: {
  task: ContributionTaskKey;
  index?: number;
  total?: number;
  positionLabel?: ReactNode;
  progress?: number;
  itemId?: string | null;
  onBack?: () => void;
  meta?: ReactNode;
  extraGuidelines?: string[];
}) {
  const { copy, lang } = useKitCopy();
  const navigate = useNavigate();
  const taskLabel = findContributorTask(lang, task).label;
  const focusMode = useTaskFocusMode();
  const position = index != null && total != null ? taskPosition(index, total) : null;
  const hasPosition = position !== null && position.total > 0;

  return (
    <header className="space-y-3">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onBack ?? (() => navigate(`/${lang}`))}
          aria-label={copy.back}
          className="-ml-2 grid size-11 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon name="arrowLeft" className="size-5" />
        </button>
        {focusMode?.isFocused ? (
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{taskLabel}</span>
        ) : (
          <TaskSwitcher task={task} className="min-w-0 flex-1 sm:flex-none" />
        )}
        {focusMode ? (
          <button
            type="button"
            onClick={focusMode.toggle}
            disabled={focusMode.isChanging}
            aria-label={focusMode.isFocused ? copy.exitFocusMode : copy.enterFocusMode}
            aria-pressed={focusMode.isFocused}
            title={focusMode.isFocused ? copy.exitFocusMode : copy.enterFocusMode}
            data-testid="task-focus-toggle"
            className="grid size-11 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <Icon name={focusMode.isFocused ? "minimize2" : "maximize2"} className="size-5" />
          </button>
        ) : null}
        {focusMode?.isFocused ? null : (
          <TaskHelpPills task={task} taskLabel={taskLabel} itemId={itemId} extraGuidelines={extraGuidelines} className="ml-auto" />
        )}
      </div>

      {hasPosition || meta || progress != null ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
            {hasPosition ? (
              <span className="font-medium tabular-nums text-foreground" aria-live="polite">
                {positionLabel ?? fillCopy(copy.position, { current: position.current, total: position.total })}
              </span>
            ) : (
              <span />
            )}
            {meta ? <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">{meta}</div> : null}
          </div>
          {progress != null ? (
            <div
              role="progressbar"
              aria-label={copy.progressLabel}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
              className="h-1 overflow-hidden rounded-full bg-surface-2"
            >
              <div className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
            </div>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
